import type { JSX, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { lireChecklist, noteBornee, pourcentageBorne } from '@taki/data-model';
import { IconCheck, IconClose } from '../icons/index.js';
import { cn } from '../lib/cn.js';
import { couleurCss } from '../lib/couleurs.js';
import {
  dureeDePeriode,
  enJauge,
  formatDeNombre,
  lignesDeCellule,
  lireBooleen,
  maxDeNote,
  texteDAvancement,
  texteDePeriode,
  texteDeDateHeure,
} from './champs-types.js';
import type { Cell, Option, ReglagesDeColonne } from './contract.js';
import type { TypeDeChamp } from './fields.js';

/**
 * LES RENDUS DES TYPES DE CHAMP : ce qu'une case MONTRE, par type, dans la
 * fiche, le tableau et la liste (`docs/socle/champs-types.md`).
 *
 * DES PRIMITIVES, ET RIEN QUE CA. Une pastille, une jauge, des etoiles, une
 * liste de cases : de quoi rendre une valeur, interactives quand l'appelant
 * leur passe un `onChange`, en lecture sinon. Elles ne savent ni ecrire, ni
 * quand ecrire : la fiche (`core/ChampsRiches.tsx`), le tableau et la fenetre
 * de lot decident, comme pour tous les editeurs du socle (`CellEditor.tsx`).
 *
 * CE MODULE N'IMPORTE RIEN DE `views/core/` EXPRES : `colonnesDe`
 * (`fields.ts`) y prend le rendu de ses colonnes, et la fiche importe
 * `fields.ts`. Un import de la fiche ici fermerait la boucle.
 *
 * LES CLASSES SONT CELLES DE LA MAQUETTE VALIDEE (atelier du 27/10/2026,
 * `docs/maquettes/atelier-2610/champs-a-rajouter.png`), elles-memes reprises
 * des etiquettes du mode selection (`components/SelectionDeFiche.tsx`) : fin
 * contour gris, texte de 12 px, point de la couleur de l'option.
 */

/* ------------------------------------------------------------------ */
/* La pastille                                                          */
/* ------------------------------------------------------------------ */

/** La pastille d'une valeur : contour fin, texte de 12 px. */
export const CLASSES_PASTILLE =
  'inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-sm border border-border bg-surface px-2 py-0.5 text-xs font-medium leading-4 text-ink-700';

/**
 * UNE PASTILLE : un mot, le point de sa couleur quand elle en a une (une option
 * de catalogue), la croix qui la retire quand l'appelant le permet.
 */
export function Pastille({
  libelle,
  couleur,
  attenue = false,
  titre,
  retirer,
  className,
  children,
}: {
  libelle: string;
  /** La couleur de l'option (`Tone`). Absente : pas de point (une etiquette libre). */
  couleur?: Option['tone'];
  /** Une option archivee, une fiche liee introuvable : le mot s'attenue. */
  attenue?: boolean;
  /** Le mot au survol (la raison d'une attenuation). */
  titre?: string;
  /** Retirer cette valeur. Absent : pas de croix, la pastille se lit. */
  retirer?: () => void;
  /** Un geste de plus, apres le mot (la fleche d'une fiche liee). */
  children?: ReactNode;
  /** Un habit de plus : la pastille d'une fiche liee prend la teinte de l'accent. */
  className?: string;
}): JSX.Element {
  return (
    <span className={cn(CLASSES_PASTILLE, retirer !== undefined && 'pr-1', className)} {...(titre === undefined ? {} : { title: titre })}>
      {couleur !== undefined && (
        <span aria-hidden className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: couleurCss(couleur) }} />
      )}
      <span className={cn('min-w-0 truncate', attenue && 'text-ink-400')}>{libelle}</span>
      {children}
      {retirer !== undefined && (
        <button
          type="button"
          aria-label={`Retirer ${libelle}`}
          onClick={(event) => {
            event.stopPropagation();
            retirer();
          }}
          className="flex h-4 w-4 shrink-0 items-center justify-center rounded-xs text-ink-400 hover:bg-surface-3 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border"
        >
          <IconClose size={9} />
        </button>
      )}
    </span>
  );
}

/** Une rangee de pastilles, qui revient a la ligne. */
export function RangeeDePastilles({ children }: { children: ReactNode }): JSX.Element {
  return <span className="inline-flex min-w-0 max-w-full flex-wrap items-center gap-1.5">{children}</span>;
}

/**
 * LES PASTILLES D'UN CHOIX : chaque clef par le libelle et la couleur de son
 * option. Une clef hors catalogue reste lisible, a son nom et sans couleur :
 * la masquer la ferait disparaitre de l'ecran, pas de la donnee.
 */
export function PastillesDeChoix({ cles, options }: { cles: readonly string[]; options: readonly Option[] }): JSX.Element {
  return (
    <RangeeDePastilles>
      {cles.map((cle) => {
        const option = options.find((candidate) => candidate.value === cle);
        return (
          <Pastille
            key={cle}
            libelle={option?.label ?? cle}
            couleur={option?.tone ?? 'neutral'}
            attenue={option?.archived === true}
          />
        );
      })}
    </RangeeDePastilles>
  );
}

/* ------------------------------------------------------------------ */
/* La jauge d'un pourcentage                                            */
/* ------------------------------------------------------------------ */

/** Le pas d'un clic ou d'une fleche sur la jauge : 5 points, 1 avec Maj. */
const PAS_DE_JAUGE = 5;

/**
 * LA JAUGE D'UN POURCENTAGE, et son nombre. Reglable quand l'appelant passe
 * `onChange` : un clic pose la valeur a l'endroit vise (arrondie a 5), les
 * fleches avancent de 5 (de 1 avec Maj), Debut et Fin vont a 0 et a 100. C'est
 * un `slider` au sens de l'accessibilite, atteignable au clavier.
 */
export function Jauge({
  valeur,
  onChange,
  libelle,
}: {
  valeur: number | null;
  onChange?: (suivante: number) => void;
  /** Le nom accessible (le libelle du champ). */
  libelle: string;
}): JSX.Element {
  const borne = pourcentageBorne(valeur) ?? 0;
  const regler = (suivante: number): void => {
    const bornee = pourcentageBorne(Math.round(suivante)) ?? 0;
    if (bornee !== valeur) onChange?.(bornee);
  };
  const auClic = (event: MouseEvent<HTMLSpanElement>): void => {
    event.stopPropagation();
    /* UN CLIC AU CLAVIER (Entree sur la piste) n'a pas de position : il ne
       doit pas poser zero. */
    if (event.detail === 0) return;
    const cadre = event.currentTarget.getBoundingClientRect();
    if (cadre.width === 0) return;
    regler(Math.round((((event.clientX - cadre.left) / cadre.width) * 100) / PAS_DE_JAUGE) * PAS_DE_JAUGE);
  };
  const auClavier = (event: KeyboardEvent<HTMLSpanElement>): void => {
    const pas = event.shiftKey ? 1 : PAS_DE_JAUGE;
    const suivante =
      event.key === 'ArrowRight' || event.key === 'ArrowUp' ? borne + pas
      : event.key === 'ArrowLeft' || event.key === 'ArrowDown' ? borne - pas
      : event.key === 'Home' ? 0
      : event.key === 'End' ? 100
      : null;
    if (suivante === null) return;
    event.preventDefault();
    event.stopPropagation();
    regler(suivante);
  };
  const piste = (
    <span
      aria-hidden
      className="relative block h-1.5 w-full overflow-hidden rounded-pill bg-border"
    >
      <span
        className="absolute inset-y-0 left-0 rounded-pill bg-accent-dot transition-[width]"
        style={{ width: `${borne}%`, transitionDuration: 'var(--dur-base)' }}
      />
    </span>
  );
  return (
    <span className="flex w-full min-w-[6rem] max-w-[16rem] items-center gap-3">
      {onChange === undefined ? (
        <span className="flex-1">{piste}</span>
      ) : (
        <span
          role="slider"
          tabIndex={0}
          aria-label={libelle}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={borne}
          aria-valuetext={`${borne} %`}
          onClick={auClic}
          onKeyDown={auClavier}
          className="flex h-5 flex-1 cursor-pointer items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border"
        >
          {piste}
        </span>
      )}
      <span className={cn('w-12 shrink-0 text-right tabular-nums', valeur === null && 'text-ink-300')}>
        {valeur === null ? '-' : `${borne.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`}
      </span>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Les etoiles d'une note                                               */
/* ------------------------------------------------------------------ */

function Etoile({ pleine }: { pleine: boolean }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden className={pleine ? 'text-ink-900' : 'text-border-strong'}>
      <path fill="currentColor" d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4l-5.9 3.1 1.2-6.5L2.5 9.4l6.6-.9z" />
    </svg>
  );
}

/**
 * LES ETOILES D'UNE NOTE (`max`, 5 par defaut). Reglables quand l'appelant
 * passe `onChange` : un clic sur la n-ieme pose n, un clic sur la note deja
 * posee l'efface (une note mise par erreur doit pouvoir s'enlever sans
 * clavier). En lecture, une image nommee « 4 sur 5 ».
 */
export function Etoiles({
  valeur,
  max,
  onChange,
  libelle,
}: {
  valeur: number | null;
  max: number;
  onChange?: (suivante: number | null) => void;
  libelle: string;
}): JSX.Element {
  const note = noteBornee(valeur, max) ?? 0;
  const rangs = Array.from({ length: max }, (_, index) => index + 1);
  if (onChange === undefined) {
    return (
      <span role="img" aria-label={valeur === null ? `${libelle} : sans note` : `${note} sur ${max}`} className="flex items-center gap-0.5">
        {rangs.map((rang) => <Etoile key={rang} pleine={rang <= note} />)}
      </span>
    );
  }
  return (
    <span role="group" aria-label={libelle} className="flex items-center gap-0.5">
      {rangs.map((rang) => (
        <button
          key={rang}
          type="button"
          aria-label={`${rang} sur ${max}`}
          aria-pressed={valeur !== null && rang <= note}
          onClick={(event) => {
            event.stopPropagation();
            onChange(valeur !== null && rang === note ? null : rang);
          }}
          className="rounded-xs p-0.5 hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border"
        >
          <Etoile pleine={rang <= note} />
        </button>
      ))}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* La liste de controle                                                 */
/* ------------------------------------------------------------------ */

/** La case d'un element : cochee (l'accent) ou non. */
export function CaseDeControle({ faite }: { faite: boolean }): JSX.Element {
  return (
    <span
      aria-hidden
      className={cn(
        'flex h-4 w-4 shrink-0 items-center justify-center rounded-xs border',
        faite ? 'border-accent-border bg-accent text-accent-label' : 'border-border-strong bg-surface',
      )}
    >
      {faite && <IconCheck size={10} />}
    </span>
  );
}

/** Une liste de controle EN LECTURE : ses cases, puis « 2 sur 3 ». */
export function ListeDeControleLue({ valeur }: { valeur: Cell }): JSX.Element {
  const elements = lireChecklist(lignesDeCellule(valeur));
  return (
    <span className="flex flex-col gap-1">
      {elements.map((element, index) => (
        <span key={`${index}-${element.texte}`} className="flex items-center gap-2 text-sm">
          <CaseDeControle faite={element.fait} />
          <span className={element.fait ? 'text-ink-400 line-through' : 'text-ink-900'}>{element.texte}</span>
        </span>
      ))}
      <span className="text-xs text-ink-400">{texteDAvancement(valeur)}</span>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* La periode, le calcul, le numero                                     */
/* ------------------------------------------------------------------ */

/** UNE PERIODE : « 05/11/2026 → 04/11/2027 », puis sa duree, attenuee (« 1 an »). */
export function PeriodeLue({ valeur }: { valeur: Cell }): JSX.Element | string {
  const texte = texteDePeriode(valeur);
  if (texte === '') return typeof valeur === 'string' ? valeur : '';
  const [debut, fin] = texte.split(' → ');
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 tabular-nums">
      {debut}
      <span aria-label="au" className="text-ink-300">→</span>
      {fin}
      <span className="text-xs text-ink-400">{dureeDePeriode(valeur)}</span>
    </span>
  );
}

/** La formule lisible, ecrite comme la maquette : « × » pour `*`, « ÷ » pour `/`. */
export function formuleAffichee(formule: string): string {
  return formule.replace(/\s*\*\s*/g, ' × ').replace(/\s*\/\s*/g, ' ÷ ');
}

/** UN CALCUL : sa valeur, et le rappel DISCRET de sa formule (« = Prime annuelle × 1,2 »). */
export function CalculLu({ texte, formule }: { texte: string; formule?: string }): JSX.Element {
  return (
    <span className="inline-flex min-w-0 flex-wrap items-baseline gap-x-2">
      <span className={cn('tabular-nums', texte === '' && 'text-ink-300')}>{texte === '' ? 'Pas encore calculé' : texte}</span>
      {formule !== undefined && <span className="text-xs text-ink-400">= {formuleAffichee(formule)}</span>}
    </span>
  );
}

/** Le mot d'une case de numero automatique VIDE : il sera donne par le serveur. */
export const NUMERO_A_VENIR = 'Attribué à l’enregistrement';

/* ------------------------------------------------------------------ */
/* Le rendu d'une colonne, par type                                     */
/* ------------------------------------------------------------------ */

/**
 * LE RENDU D'UNE VALEUR, PAR TYPE, tel que `colonnesDe` le pose sur une
 * colonne (`Column.render`) : une case de tableau, une ligne de liste, et la
 * valeur d'une case de fiche en lecture. `undefined` quand le type n'a pas de
 * rendu a lui : la colonne garde alors EXACTEMENT son affichage d'avant (un
 * texte, une date, un lien que l'annuaire nomme, un nombre nu).
 *
 * UNE CASE VIDE REND `''` : c'est ce que la fiche lit pour dire « Non
 * renseigne » (ou inviter, sur un brouillon), jamais une pastille vide.
 *
 * SEULEMENT DU CONTENU DE PHRASE (des `span`) : une case editable de la fiche
 * pose ce rendu DANS son bouton, qui n'accepte ni bloc ni element cliquable.
 */
export function rendreValeurDeChamp(
  type: TypeDeChamp,
  reglages: ReglagesDeColonne | undefined,
  options?: readonly Option[],
): ((valeur: Cell) => ReactNode) | undefined {
  const vide = (valeur: Cell): boolean => valeur === null || valeur === '';
  const nombre = formatDeNombre(type, reglages);
  switch (type) {
    case 'boolean':
      return (valeur) => {
        const lu = lireBooleen(valeur);
        return lu === null ? '' : lu ? 'Oui' : 'Non';
      };
    case 'select':
      return (valeur) => (vide(valeur) ? '' : <PastillesDeChoix cles={[String(valeur)]} options={options ?? []} />);
    case 'multiselect':
      return (valeur) => {
        const cles = String(valeur ?? '').split(/[\n,]/).map((cle) => cle.trim()).filter((cle) => cle !== '');
        return cles.length === 0 ? '' : <PastillesDeChoix cles={cles} options={options ?? []} />;
      };
    case 'etiquettes':
      return (valeur) => {
        const etiquettes = lignesDeCellule(valeur);
        return etiquettes.length === 0 ? '' : (
          <RangeeDePastilles>
            {etiquettes.map((etiquette) => <Pastille key={etiquette} libelle={etiquette} />)}
          </RangeeDePastilles>
        );
      };
    case 'note':
      return (valeur) => (typeof valeur === 'number' ? <Etoiles valeur={valeur} max={maxDeNote(reglages)} libelle="" /> : '');
    case 'periode':
      return (valeur) => (vide(valeur) ? '' : <PeriodeLue valeur={valeur} />);
    case 'checklist':
      /* EN CASE DE TABLEAU, L'AVANCEMENT SEUL (« 2 sur 3 ») : la liste
         entiere ferait d'une ligne un paragraphe. La fiche, elle, rend ses
         cases (`core/ChampsRiches.tsx`). */
      return (valeur) => texteDAvancement(valeur);
    case 'datetime':
      /* UNE DATE-HEURE SE LIT AVEC SON HEURE, dans le fuseau de l'org, qu'elle
         soit une `Date` (champ natif) ou l'instant ISO que le sac range
         (`texteDeDateHeure`). Un texte qui n'est pas un instant reste tel quel. */
      return (valeur) => (vide(valeur) ? '' : texteDeDateHeure(valeur, reglages?.fuseau) ?? String(valeur));
    case 'numero':
      return (valeur) => (vide(valeur) ? '' : <span className="font-mono text-sm tabular-nums text-ink-700">{String(valeur)}</span>);
    case 'number':
      /* UN NOMBRE EN JAUGE SE LIT EN JAUGE (`affichage: 'jauge'`) ; tout autre
         nombre par son format (`formatDeNombre`), montant, pourcentage ou
         duree compris. */
      if (enJauge(type, reglages)) return (valeur) => (typeof valeur === 'number' ? <Jauge valeur={valeur} libelle="" /> : '');
      return nombre === undefined ? undefined : (valeur) => (typeof valeur === 'number' ? nombre(valeur) : vide(valeur) ? '' : String(valeur));
    case 'calcul':
      return nombre === undefined ? undefined : (valeur) => (typeof valeur === 'number' ? nombre(valeur) : vide(valeur) ? '' : String(valeur));
    default:
      return undefined;
  }
}
